import { get } from "svelte/store";
import type { CPMOutcome, Task, WbsNode } from "./types";
import type { PhaseDef } from "./phases";
import { allowWeekendWork, det, projectPhases, projectStartDate, showToast, tasks, twoShifts, wbsNodes } from "./stores";
import { taskShiftsPerDay } from "./cpm";
import { dateOfProjectDay } from "./projectDate";
import { downloadFile } from "./download";

/**
 * P6 XML export (the `p6apibo.xsd` shape Primavera Cloud/P6 import as "P6 XML or XER
 * Data"). ObjectIds are sequential integers assigned here — P6's own internal key
 * space — kept separate from each object's human-facing `Id`/`Code`, which reuses this
 * app's existing stable IDs (task id, WBS code).
 *
 * This module only ever produces a file for someone to load into Primavera — the
 * reverse direction (reading a P6 XML file back in) lives in `p6xmlImport.ts`, used
 * only when starting a brand-new planning, and is tolerant of files this app never
 * wrote itself rather than assuming the exact shape below.
 */

const ROOT_WBS_ID = "__root__";
const PROJECT_OBJECT_ID = 1;
const CALENDAR_OBJECT_ID = 1;
/** The one Activity Code Type this export ever writes — P6's closest native
 *  equivalent to this app's phase swimlanes. `p6xmlImport.ts` looks for an
 *  Activity Code Type named exactly this to read swimlanes back. */
const PHASE_CODE_TYPE_NAME = "Phase";
const PHASE_CODE_TYPE_OBJECT_ID = 1;

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function tag(name: string, value: string | number | null | undefined): string {
  if (value == null || value === "") return "";
  return `<${name}>${typeof value === "number" ? value : escapeXml(value)}</${name}>`;
}

/** "YYYY-MM-DD" (projectDate.ts's civil-date format) as a P6 XML datetime. */
function p6DateTime(startIso: string, day: number): string | null {
  const d = dateOfProjectDay(startIso, day);
  if (!d) return null;
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mo}-${da}T00:00:00`;
}

function calendarXml(allowWeekend: boolean): string {
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const workWeek = days
    .map((day, i) => {
      const working = allowWeekend || i < 5;
      const hours = working ? `<WorkTime><Start>07:00</Start><Finish>15:00</Finish></WorkTime>` : "";
      return `<WorkWeekDay><Day>${day}</Day>${hours}</WorkWeekDay>`;
    })
    .join("");
  return [
    `<Calendar>`,
    tag("ObjectId", CALENDAR_OBJECT_ID),
    tag("Name", allowWeekend ? "7-day calendar" : "5-day calendar"),
    tag("IsDefault", "true"),
    tag("Type", "Global"),
    `<StandardWorkWeek>${workWeek}</StandardWorkWeek>`,
    `</Calendar>`,
  ].join("");
}

function projectXml(startIso: string): string {
  return [
    `<Project>`,
    tag("ObjectId", PROJECT_OBJECT_ID),
    tag("Id", "CRANE-CHANGEOVER"),
    tag("Name", "Crane changeover planning"),
    tag("DefaultCalendarObjectId", CALENDAR_OBJECT_ID),
    tag("PlannedStartDate", p6DateTime(startIso, 0)),
    `</Project>`,
  ].join("");
}

function wbsXml(nodes: WbsNode[], objectIdOf: Map<string, number>): string {
  return nodes
    .map((n) => {
      const parentObjectId = n.parentId ? objectIdOf.get(n.parentId) : undefined;
      return [
        `<WBS>`,
        tag("ObjectId", objectIdOf.get(n.id)!),
        tag("ProjectObjectId", PROJECT_OBJECT_ID),
        tag("Code", n.code),
        tag("Name", n.name),
        parentObjectId != null ? tag("ParentObjectId", parentObjectId) : "",
        `</WBS>`,
      ].join("");
    })
    .join("");
}

/** The project's phase swimlanes, mapped onto a single P6 Activity Code Type named
 *  "Phase" with one Activity Code value per lane — P6 has no native "swimlane"
 *  concept, but Activity Codes are exactly what they're for. A phase used by some
 *  task but missing from the project's own swimlane list (renamed/deleted since,
 *  same case `BoardTable` handles) still gets a code of its own, so no activity's
 *  lane is silently dropped from the export. */
function exportedPhases(tasksIn: Task[], projectPhasesIn: PhaseDef[]): PhaseDef[] {
  const known = new Set(projectPhasesIn.map((p) => p.name));
  const orphanNames = [...new Set(tasksIn.map((t) => t.phase))].filter((p) => p && !known.has(p));
  return [...projectPhasesIn, ...orphanNames.map((name) => ({ name, color: "" }))];
}

function activityCodeTypeXml(): string {
  return [
    `<ActivityCodeType>`,
    tag("ObjectId", PHASE_CODE_TYPE_OBJECT_ID),
    tag("Name", PHASE_CODE_TYPE_NAME),
    tag("Scope", "Project"),
    tag("ProjectObjectId", PROJECT_OBJECT_ID),
    `</ActivityCodeType>`,
  ].join("");
}

function activityCodeXml(phases: PhaseDef[], objectIdOf: Map<string, number>): string {
  return phases
    .map(
      (p, i) =>
        `<ActivityCode>` +
        tag("ObjectId", objectIdOf.get(p.name)!) +
        tag("CodeTypeObjectId", PHASE_CODE_TYPE_OBJECT_ID) +
        tag("CodeValue", p.name) +
        tag("Color", /^#/.test(p.color) ? p.color : null) +
        tag("SequenceNumber", i + 1) +
        `</ActivityCode>`
    )
    .join("");
}

/** Subcontractor/equipment/personnel/risk have no P6 field of their own — carried as free-text activity notes instead. */
function activityNotes(t: Task): string {
  const lines: string[] = [];
  if (t.subcontractors.length) lines.push(`Subcontractors: ${t.subcontractors.join(", ")}`);
  if (t.responsibleParty) lines.push(`Responsible party: ${t.responsibleParty}`);
  if (t.requiredEquipment) lines.push(`Required equipment: ${t.requiredEquipment}`);
  if (t.requiredPersonnel) lines.push(`Required personnel: ${t.requiredPersonnel}`);
  if (t.risk) lines.push(`Linked risk: ${t.risk}`);
  return lines.join("\n");
}

function activityXml(
  t: Task,
  startIso: string,
  cpm: CPMOutcome & { ok: true },
  activityObjectIdOf: Map<string, number>,
  wbsObjectIdOf: Map<string, number>,
  phaseObjectIdOf: Map<string, number>,
  projectShiftsPerDay: 1 | 2
): string {
  const wbsObjectId = t.wbsId ? wbsObjectIdOf.get(t.wbsId) : wbsObjectIdOf.get(ROOT_WBS_ID);
  const phaseObjectId = phaseObjectIdOf.get(t.phase);
  const durationDays = cpm.remaining[t.id] ?? 0;
  const plannedCalendarDays = t.m / taskShiftsPerDay(t, projectShiftsPerDay);
  const percentComplete = plannedCalendarDays > 0 ? Math.min(1, Math.max(0, t.doneDays.length / plannedCalendarDays)) : 0;
  const notes = activityNotes(t);
  return [
    `<Activity>`,
    tag("ObjectId", activityObjectIdOf.get(t.id)!),
    tag("ProjectObjectId", PROJECT_OBJECT_ID),
    tag("Id", t.id),
    tag("Name", t.name),
    tag("Type", t.type === "milestone" ? "Finish Milestone" : "Task Dependent"),
    wbsObjectId != null ? tag("WBSObjectId", wbsObjectId) : "",
    tag("PlannedStartDate", p6DateTime(startIso, cpm.ES[t.id])),
    tag("PlannedFinishDate", p6DateTime(startIso, cpm.EF[t.id])),
    tag("PlannedDuration", durationDays * 8),
    tag("PercentComplete", Math.round(percentComplete * 100) / 100),
    notes ? `<Notes>${escapeXml(notes)}</Notes>` : "",
    phaseObjectId != null ? `<Code>${tag("TypeObjectId", PHASE_CODE_TYPE_OBJECT_ID)}${tag("ValueObjectId", phaseObjectId)}</Code>` : "",
    `</Activity>`,
  ].join("");
}

function relationshipXml(t: Task, activityObjectIdOf: Map<string, number>): string {
  return t.preds
    .map((predId) => {
      const predObjectId = activityObjectIdOf.get(predId);
      const succObjectId = activityObjectIdOf.get(t.id);
      if (predObjectId == null || succObjectId == null) return "";
      return [
        `<Relationship>`,
        tag("PredecessorActivityObjectId", predObjectId),
        tag("SuccessorActivityObjectId", succObjectId),
        tag("PredecessorProjectObjectId", PROJECT_OBJECT_ID),
        tag("SuccessorProjectObjectId", PROJECT_OBJECT_ID),
        tag("Type", "Finish to Start"),
        `</Relationship>`,
      ].join("");
    })
    .join("");
}

/**
 * Pure XML builder — takes the board's own CPM result (`ok: true` only; callers must
 * check `.ok` first, same as every other CPMOutcome consumer) so exported dates always
 * match what's on screen, and so tests can pass a fixed schedule without going through
 * the `tasks`/`det` stores.
 */
export function buildP6Xml(
  tasksIn: Task[],
  wbsNodesIn: WbsNode[],
  projectPhasesIn: PhaseDef[],
  projectStartIso: string,
  allowWeekend: boolean,
  cpm: CPMOutcome & { ok: true },
  shiftsPerDay: 1 | 2 = 1
): string {
  const nodes: WbsNode[] = [{ id: ROOT_WBS_ID, code: "ROOT", name: "Crane changeover planning", parentId: null }, ...wbsNodesIn];
  const wbsObjectIdOf = new Map<string, number>(nodes.map((n, i) => [n.id, i + 1]));
  const activityObjectIdOf = new Map<string, number>(tasksIn.map((t, i) => [t.id, i + 1]));
  const phases = exportedPhases(tasksIn, projectPhasesIn);
  const phaseObjectIdOf = new Map<string, number>(phases.map((p, i) => [p.name, i + 1]));

  const body = [
    projectXml(projectStartIso),
    calendarXml(allowWeekend),
    wbsXml(nodes, wbsObjectIdOf),
    activityCodeTypeXml(),
    activityCodeXml(phases, phaseObjectIdOf),
    tasksIn.map((t) => activityXml(t, projectStartIso, cpm, activityObjectIdOf, wbsObjectIdOf, phaseObjectIdOf, shiftsPerDay)).join(""),
    tasksIn.map((t) => relationshipXml(t, activityObjectIdOf)).join(""),
  ].join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<APIBusinessObjects xmlns="http://xmlns.oracle.com/Primavera/P6/V23.12/API/BusinessObjects">${body}</APIBusinessObjects>`;
}

/** Loaded on demand — reads the same stores and CPM result the board itself renders from. */
export async function exportP6Xml(): Promise<void> {
  const startIso = get(projectStartDate);
  if (!startIso) {
    showToast("Set a project start date before exporting to Primavera");
    return;
  }
  const cpm = get(det);
  if (!cpm.ok) {
    showToast(`Can't export to Primavera: ${cpm.error}`);
    return;
  }
  const xml = buildP6Xml(get(tasks), get(wbsNodes), get(projectPhases), startIso, get(allowWeekendWork), cpm, get(twoShifts) ? 2 : 1);
  await downloadFile("crane-changeover-planning-p6.xml", xml, "application/xml");
}
